import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    BimContractObjectData,
    BimContractObjectDataFilterInput,
    BimContractObjectDataSortInput,
} from 'gql/graphql';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimContractObjectsGql } from '../BimContractStructureRequests';

export const useBimContractObjects = ({
    contractId,
    constructionTypeId,
    where,
    order,
}: {
    contractId: number;
    constructionTypeId: number | undefined;
    where?: BimContractObjectDataFilterInput;
    order?: BimContractObjectDataSortInput[];
}) => {
    const dispatch = useDispatch();

    const variables = useMemo(
        () => ({ contractId, constructionTypeId, where, order }),
        [constructionTypeId, contractId, order, where],
    );

    const [bimContractObjectsData, bimContractObjectsStatus, bimContractObjectsError, bimContractObjectsRequest] =
        useRequest({
            requestId: 'bimContractObjects',
            query: bimContractObjectsGql,
            variables,
            withEffect: Boolean(constructionTypeId),
        });

    const data: BimContractObjectData[] = useMemo(
        () => bimContractObjectsData?.bimContractObjects,
        [bimContractObjectsData?.bimContractObjects],
    );

    const bimContractObjectsMakeRequest = useCallback(() => {
        dispatch(bimContractObjectsRequest(variables));
    }, [bimContractObjectsRequest, dispatch, variables]);

    const [resetBimContractObjects] = useResetStoreData({ requestId: 'bimContractObjects' });

    return {
        bimContractObjectsData: data,
        bimContractObjectsStatus,
        bimContractObjectsError,
        bimContractObjectsRequest,
        bimContractObjectsMakeRequest,
        resetBimContractObjects,
    };
};
